















































































import StatsCard from '@/components/StatsCard.vue';
import { copyToClipBoard } from '@/helpers/copyToClipBoard';
import { DataTableHeader } from 'vuetify';
import { mapGetters } from 'vuex';
import {
  DateRangeParams,
  ETipsStatsSearchBy,
  ITotalTipsStatsResponse,
  ITotalTipsStatsPayload,
  ValidationRules,
  ITipsStatsHistoryResponse,
  ITipsStatsHistoryPayload,
  ITipsStatsHistoryRecord
} from '@/api/schema';
import { defaultDates, MIN_DATE } from '@/helpers/copyStakeHelpers';
import VExtendedDataPicker from '@/components/VExtendedDataPicker.vue';

import { doDownload } from '@/helpers/download';
import * as vr from '@/helpers/validation';
import { errorToastMessage } from '@/helpers/errorToastMessage';
import {
  getCopyStakeTipsStats,
  getCopyStakeTipsStatsCsv,
  getCopyStakeTotalTipsStats
} from '@/api/CopyStake/TipsStats';

const CopyAddress = (): Promise<any> =>
  import('@/components/CopyAddress.vue');

interface ISelectItem {
  text: string;
  value: ETipsStatsSearchBy;
}

interface IFormattedTipsStatsRecord {
  created_at: string;
  viewer_amount: number;
  viewer_currency: string;
  streamer_amount: number;
  streamer_currency: string;
  viewer_user_id: string;
  'str.nickname': string;
  session_id: string;
}

type TCopyStakeTipsTableState = {
  MIN_DATE: string;
  dates: DateRangeParams;
  totalTipsValue: number;
  tipsStatsRecords: ITipsStatsHistoryRecord[];
  totalTipsStatsRecords: number;
  tableOptions: {
    page: number;
    itemsPerPage: number;
  };
  tmpSearchOptions: {
    searchBy: ETipsStatsSearchBy;
    search: string;
  };
  searchOptions: {
    searchBy: ETipsStatsSearchBy;
    search: string;
  };
  loadingState: {
    totalTips: boolean;
    list: boolean;
    query: boolean;
    csv: boolean;
  };
};

const DEFAULT_PAGINATION_OPTIONS = {
  page: 1,
  itemsPerPage: 10,
  sortBy: ['created_at'],
  sortDesc: [true]
} as const;

const DEFAULT_SEARCH_OPTIONS = {
  searchBy: ETipsStatsSearchBy.STREAMER_NICKNAME,
  search: ''
} as const;

export default {
  name: 'CopyStakeStatsTipsTable',
  components: {
    StatsCard,
    VExtendedDataPicker,
    CopyAddress
  },
  data(): TCopyStakeTipsTableState {
    return {
      MIN_DATE,
      tableOptions: { ...DEFAULT_PAGINATION_OPTIONS },
      tmpSearchOptions: { ...DEFAULT_SEARCH_OPTIONS },
      searchOptions: { ...DEFAULT_SEARCH_OPTIONS },
      dates: { ...defaultDates },

      totalTipsValue: 0,
      tipsStatsRecords: [],
      totalTipsStatsRecords: 0,

      loadingState: {
        totalTips: false,
        list: false,
        query: false,
        csv: false
      }
    };
  },
  watch: {
    operatorId: {
      async handler(): Promise<void> {
        this.fetchTotalTipsStats();
        this.fetchTipsStatsRecords();
      }
    },
    totalTipsRequestParams: {
      async handler(): Promise<void> {
        await this.fetchTotalTipsStats();
      },
      deep: true,
      immediate: true
    },
    tipsStatsRequestParams: {
      async handler(): Promise<void> {
        await this.fetchTipsStatsRecords();
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters('Onboarding', ['operatorId']),
    headers(): DataTableHeader[] {
      return [
        { text: 'Date & Time', value: 'created_at' },
        { text: 'Amount', value: 'viewer_amount' },
        { text: 'Currency', value: 'viewer_currency' },
        { text: 'Converted Amount', value: 'streamer_amount' },
        { text: 'Converted Currency', value: 'streamer_currency' },
        { text: 'Operator User ID', value: 'viewer_user_id' },
        { text: 'Streamer', value: 'str.nickname' },
        { text: 'Session ID', value: 'session_id' }
      ];
    },
    formattedTipsStatsRecords(): IFormattedTipsStatsRecord[] {
      return this.tipsStatsRecords.map(
        ({
          createdAt,
          originalAmount,
          originalCurrency,
          convertedAmount,
          convertedCurrency,
          operatorUserId,
          streamerNickname,
          sessionId
        }: ITipsStatsHistoryRecord) => ({
          created_at: createdAt,
          viewer_amount: originalAmount,
          viewer_currency: originalCurrency,
          streamer_amount: convertedAmount,
          streamer_currency: convertedCurrency,
          viewer_user_id: operatorUserId,
          'str.nickname': streamerNickname,
          session_id: sessionId
        })
      );
    },
    formattedTotalTips(): string {
      return `${this.$options.filters.numeralSpaces(
        this.totalTipsValue || 0,
        '0,0.[00]'
      )} USD`;
    },
    searchByOptions(): ISelectItem[] {
      return [
        {
          text: 'Streamer',
          value: ETipsStatsSearchBy.STREAMER_NICKNAME
        }
      ];
    },
    currentSearchBy(): ISelectItem | null {
      return (
        this.searchByOptions.find(
          (option: ISelectItem) =>
            option.value === this.tmpSearchOptions.searchBy
        ) ?? null
      );
    },
    searchPlaceholder(): string {
      if (!this.currentSearchBy) {
        return '';
      }

      return `Search by ${this.currentSearchBy.text}`;
    },
    isLoading(): boolean {
      return Object.values(this.loadingState).some(Boolean);
    },
    rules(): ValidationRules {
      return {
        [ETipsStatsSearchBy.STREAMER_NICKNAME]: [
          (v) => (v ? vr.minLength(v, 3) : true)
        ]
      };
    },
    getSearchRules(): ValidationRules {
      return this.rules[this.currentSearchBy.value];
    },
    totalTipsRequestParams(): ITotalTipsStatsPayload {
      return {
        ...(this.searchOptions.search
          ? { [this.searchOptions.searchBy]: this.searchOptions.search }
          : {}),
        fromDate: this.dates.from,
        toDate: this.dates.to
      };
    },
    tipsStatsRequestParams(): ITipsStatsHistoryPayload {
      return {
        ...this.totalTipsRequestParams,
        size: this.tableOptions.itemsPerPage,
        page: this.tableOptions.page - 1,
        sort: this.tableOptions.sortBy[0],
        order: this.tableOptions.sortDesc[0] ? 'DESC' : 'ASC'
      };
    }
  },
  methods: {
    copyToClipBoard,
    handleClickClearSearchInput(): void {
      this.updateRequestOptions({
        ...this.tmpSearchOptions,
        search: ''
      });
    },
    handleKeydownEnterSearchInput(): void {
      this.updateRequestOptions(this.tmpSearchOptions);
    },
    handleClickSearchButton(): void {
      this.updateRequestOptions(this.tmpSearchOptions);
    },
    updateRequestOptions(newOptions: Record<string, unknown>): void {
      const searchInput = this.$refs.searchInput;
      searchInput.validate();

      this.$nextTick((): void => {
        if (searchInput.valid) {
          if (
            this.searchOptions.search !==
            (newOptions?.search ?? DEFAULT_SEARCH_OPTIONS.search)
          ) {
            this.loadingState.query = true;
          }

          this.searchOptions.searchBy =
            newOptions.searchBy ?? DEFAULT_SEARCH_OPTIONS.searchBy;
          this.searchOptions.search =
            newOptions.search ?? DEFAULT_SEARCH_OPTIONS.search;

          this.tableOptions.page = DEFAULT_PAGINATION_OPTIONS.page;
          this.tableOptions.itemsPerPage =
            DEFAULT_PAGINATION_OPTIONS.itemsPerPage;
        }
      });
    },
    async fetchTipsStatsRecords(): Promise<void> {
      try {
        this.loadingState.list = true;

        const {
          content: tipsStatsRecords,
          totalElements: totalTipsStatsRecords
        }: ITipsStatsHistoryResponse = await getCopyStakeTipsStats(
          this.tipsStatsRequestParams
        );

        this.tipsStatsRecords = tipsStatsRecords;
        this.totalTipsStatsRecords = totalTipsStatsRecords;
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.loadingState.list = false;
        this.loadingState.query = false;
      }
    },
    async fetchTotalTipsStats(): Promise<void> {
      try {
        this.loadingState.totalTips = true;

        const { amount }: ITotalTipsStatsResponse =
          await getCopyStakeTotalTipsStats(this.totalTipsRequestParams);

        this.totalTipsValue = amount;
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.loadingState.totalTips = false;
      }
    },
    async handlerOnDownloadCSV(): Promise<void> {
      try {
        this.loadingState.csv = true;

        const data = await getCopyStakeTipsStatsCsv({
          ...this.tipsStatsRequestParams,
          size: 10000,
          page: 0
        });

        const url = window.URL.createObjectURL(new Blob([data]));
        const date = Object.values(this.dates)
          .filter((item: string) => item != null && item !== '')
          .map((item: string) => item.replaceAll('-', '.'))
          .join('-');

        doDownload(
          url,
          `copy-stake-tips-stats-report[${date ? date : 'all periods'}].csv`
        );
      } catch (error) {
        errorToastMessage(error);
      } finally {
        this.loadingState.csv = false;
      }
    }
  }
};
